import { Fragment } from "react";

import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ArrowLeftTwoToneIcon from "@mui/icons-material/ArrowLeftTwoTone";
import ArrowRightTwoToneIcon from "@mui/icons-material/ArrowRightTwoTone";
import SettingsPowerIcon from "@mui/icons-material/SettingsPower";
import _ from "lodash";

import { useMainLayoutContext } from "../MainLayout";
import MenuItem from "./MenuItem";
import { logout } from "../../../../../utilitie/Auth";
import { getFlatRoute } from "../../../../../routes/routeTool";
import { ERouteKey } from "../../../../../compiler/route.enum";
import { Link } from "react-router-dom";
import useGetFilterRoutes from "../../../../../hook/routes/useGetFilterRoutes";
import milkRunn from "../../../../../assets/svg/brand/milkRunn.svg";
import AvatarMenu from "../NavigationBar/AvatarMenu";
import VersionInfo from "../../../Widget/VersionInfo";

export interface ISideBarProps {}

export default function SideBar({}: ISideBarProps) {
  const filteredRoutes = useGetFilterRoutes();
  const {
    sideBarWidth,
    isSideBarOpen,
    toggleSideBar,
    closeSideBar,
    animateDuration,
    animateEasing,
  } = useMainLayoutContext();

  const flatRoute = getFlatRoute(filteredRoutes);

  const navigateToHomePage = () => {
    closeSideBar();
  };
  const navigateToSettingPage = () => {
    closeSideBar();
  };

  const settingPath = "/" + (flatRoute[ERouteKey.SETTING_OUTLET]?.path || "settings");

  return (
    <>
      <Box
        position={"fixed"}
        top={0}
        left={isSideBarOpen ? sideBarWidth : 0}
        sx={{
          transition: (theme) =>
            theme.transitions.create("left", {
              easing: animateEasing,
              duration: animateDuration,
            }),
          zIndex: (t) => t.zIndex.drawer + 2,
          pointerEvents: "auto",
        }}
      >
        <Button
          onClick={toggleSideBar}
          sx={{
            width: 24,
            height: 24,
            p: 2,
            minWidth: "unset",
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderColor: "divider",
            borderLeftColor: "common.white",
            borderTopColor: "common.white",
            ":hover": {
              borderLeftColor: "common.white",
              borderTopColor: "common.white",
            },
            backgroundColor: "common.white",
            ml: "-1px",
            borderLeftWidth: 0,
            borderTopWidth: 0,
          }}
          variant="outlined"
          aria-label="close side menu"
        >
          {isSideBarOpen ? <ArrowLeftTwoToneIcon /> : <ArrowRightTwoToneIcon />}
        </Button>
      </Box>
      <Drawer
        sx={{
          width: `${sideBarWidth}px`,
          overflow: "visible",
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: sideBarWidth,
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="left"
        open={isSideBarOpen}
      >
        <Link to="/" style={{ textDecoration: 'none' }} onClick={navigateToHomePage}>
          <Box
            sx={{ cursor: "pointer" }}
            component="img"
            src={milkRunn}
            p={2}
          />
        </Link>

        <List sx={{ overflowY: "auto", maxHeight: "calc(100vh - 64px)" }} disablePadding>
          {filteredRoutes
            ?.find((route) => route.key === ERouteKey.OUTLET)
            ?.children?.filter(
              (child) =>
                child.path !== "welcome" &&
                child.key !== ERouteKey.WELCOME &&
                child.key !== ERouteKey.CUSTOMER_PORTAL_OUTLET
            )
            ?.map((item, index) => {
              // Destructure to remove `key` from item before spreading
              const { key: _unusedKey, ...menuItemProps } = item;
              return (
                <MenuItem
                  key={item.key || index.toString()}
                  {...menuItemProps}
                />
              );
            })}
        </List>
        <List sx={{ mt: "auto", mb: 4 }} disablePadding>
          <Link to={settingPath} style={{ textDecoration: 'none', color: 'inherit' }} onClick={navigateToSettingPage}>
          <ListItemButton
            sx={{
              border: "1px solid transparent",
              ":hover": {
                bgcolor: "primary.light",
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
                color: "primary.dark",
                "& .MuiSvgIcon-root": {
                  color: "primary.dark",
                },
              },
              mx: 1,
              mb: 0.5,
              p: 0,
              borderRadius: 2,
              transition: (theme) =>
                theme.transitions.create("background-color", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                }),
            }}
          >
            <ListItemIcon
              sx={{ minWidth: "48px", m: 0, p: (t) => t.spacing(1.5, 2.85) }}
            >
              {flatRoute[ERouteKey.SETTING_OUTLET]?.icon}
            </ListItemIcon>
            <ListItemText
              color="text.secondary"
              primary={flatRoute[ERouteKey.SETTING_OUTLET]?.name}
            />
          </ListItemButton>
          </Link>
          <AvatarMenu />
          <VersionInfo variant="compact" />
        </List>
      </Drawer>
    </>
  );
}
